import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DirectoryRow } from "@/components/radio/StationCard";
import { api, formatApiError } from "@/lib/api";

const PAGE = 40;

export const Discover = ({ onSaved }) => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tag, setTag] = useState("");
  const [country, setCountry] = useState("all");
  const [tags, setTags] = useState([]);
  const [countries, setCountries] = useState([]);
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/radio/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/radio/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 350); return () => clearTimeout(t); }, [q]);

  const params = (offset) => ({ q: debounced, tag, country: country === "all" ? "" : country, limit: PAGE, offset });
  useEffect(() => {
    setRows(null);
    setError("");
    api.get("/radio/directory", { params: params(0) })
      .then((r) => { setRows(r.data); setMore(r.data.length >= PAGE - 5); })
      .catch((e) => { setError(formatApiError(e)); setRows([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, tag, country]);

  const loadMore = async () => {
    const { data } = await api.get("/radio/directory", { params: params(rows.length + 5) });
    setRows([...rows, ...data.filter((d) => !rows.some((r) => r.rb_uuid === d.rb_uuid))]);
    setMore(data.length >= PAGE - 5);
  };
  const save = async (s) => {
    setSaving(s.rb_uuid);
    try {
      const { data } = await api.post("/radio/stations", { rb_uuid: s.rb_uuid });
      setRows((rs) => rs.map((r) => (r.rb_uuid === s.rb_uuid ? { ...r, saved: true } : r)));
      toast.success(`${data.name} saved to My stations`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving("");
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 30,000+ stations — KEXP, BBC Radio 6, jazz…" className="h-11 pl-9" data-testid="directory-search-input" />
        </div>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="h-11 sm:w-56" data-testid="directory-country-select"><SelectValue placeholder="All countries" /></SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="all">All countries</SelectItem>
            {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="directory-tags">
        {tags.slice(0, 28).map((t) => (
          <button key={t} onClick={() => setTag(tag === t ? "" : t)} data-testid={`directory-tag-${t.replace(/\s+/g, "-")}`}
            className={`rounded-full px-3 py-1 text-xs capitalize transition-colors ${tag === t ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 ring-1 ring-zinc-200 hover:ring-zinc-400"}`}>{t}</button>
        ))}
      </div>
      <p className="mb-3 mt-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
        {debounced || tag || country !== "all" ? "Results" : "Most popular worldwide"} · via Radio Browser
      </p>
      {error && <p className="text-sm text-red-600" data-testid="directory-error">{error}</p>}
      {!rows ? <Loader2 className="animate-spin text-zinc-400" /> : rows.length === 0 && !error ? (
        <p className="py-8 text-sm text-zinc-500" data-testid="directory-empty">No stations found. Try another name or tag.</p>
      ) : (
        <div className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white" data-testid="directory-results">
          {rows.map((s) => <DirectoryRow key={s.rb_uuid} s={s} onSave={save} saving={saving === s.rb_uuid} />)}
        </div>
      )}
      {rows?.length > 0 && more && (
        <button onClick={loadMore} data-testid="directory-load-more" className="mx-auto mt-6 block rounded-full border border-zinc-300 px-6 py-2 text-sm hover:border-zinc-900">Load more</button>
      )}
    </div>
  );
};
